import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Dialog,
  MenuItem,
  Select,
  Chip,
  Box,
  Alert,
  SelectChangeEvent,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { RootState, useAppDispatch } from '../../redux/store'
import {
  selectUsers,
  selectUsersLoading,
} from '../../redux/users/usersSelectors'
import { SymboleRasci } from '../../utils/constants'
import { User } from '../../types/userTypes'
import { fetchUsers } from '../../redux/users/usersAction'
import {
  createActeurActionAsync,
  updateActeurActionAsync,
} from '../../redux/actorAction/actorActionAction'
import UseAlert from '../common/alert'
import { UserRole } from '../../types/keycloak'
import { hasRole } from '../../utils/checkRole'
import { DetailButton } from '../common/detail'
import '../common/detail/detail.css'

const EMPTY_SELECTION: {
  [key in SymboleRasci]: string[] | string | null
} = {
  [SymboleRasci.RESPONSIBLE]: [],
  [SymboleRasci.APPROVER]: null,
  [SymboleRasci.SUPPORT]: [],
  [SymboleRasci.VIEWED]: [],
  [SymboleRasci.INFORMED]: [],
}

const ROLE_META: Record<
  SymboleRasci,
  { bg: string; color: string; Icon: React.ElementType }
> = {
  [SymboleRasci.RESPONSIBLE]: {
    bg: '#FAE6E3',
    color: '#96271B',
    Icon: PersonOutlineRoundedIcon,
  },
  [SymboleRasci.APPROVER]: {
    bg: '#E1F4F0',
    color: '#076B5C',
    Icon: VerifiedOutlinedIcon,
  },
  [SymboleRasci.SUPPORT]: {
    bg: '#E6EFF8',
    color: '#22548A',
    Icon: GroupsOutlinedIcon,
  },
  [SymboleRasci.VIEWED]: {
    bg: '#FAEFDA',
    color: '#84540A',
    Icon: VisibilityOutlinedIcon,
  },
  [SymboleRasci.INFORMED]: {
    bg: '#EAF0F0',
    color: '#5F7484',
    Icon: NotificationsNoneOutlinedIcon,
  },
}

interface ActorActionProps {
  actionId: string
  isOpen: boolean
  onClose: () => void
  navigateTo?: boolean
  /** Where to go after a successful assignment. Defaults to the task list. */
  returnTo?: string
  existingRasci?: any
  isUpdate?: boolean
}

const ActorAction: React.FC<ActorActionProps> = ({
  actionId,
  isOpen,
  onClose,
  navigateTo = true,
  returnTo,
  existingRasci = null,
  isUpdate = false,
}) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const users: User[] = useSelector((state: RootState) => selectUsers(state))
  const usersLoading = useSelector(selectUsersLoading)
  const [warningMessage] = useState('')
  const [ready, setReady] = useState(false)
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })
  const [selectedUsers, setSelectedUsers] = useState<{
    [key in SymboleRasci]: string[] | string | null
  }>({ ...EMPTY_SELECTION })

  useEffect(() => {
    if (isOpen) {
      setReady(false)
      setSelectedUsers({ ...EMPTY_SELECTION })
      dispatch(fetchUsers())
    }
  }, [dispatch, isOpen])

  useEffect(() => {
    if (!isOpen) return

    // Wait until users are available (or fetch finished empty)
    if ((!users || users.length === 0) && usersLoading) return

    if (isUpdate && existingRasci?.RASCI) {
      if (!users?.length) {
        setReady(true)
        return
      }

      const findId = (username: string) =>
        users.find(
          (user) =>
            user.username?.toLocaleLowerCase() ===
            username?.toLocaleLowerCase()
        )?.id || ''

      const initialSelectedUsers = Object.entries(existingRasci.RASCI).reduce(
        (acc, [role, usernames]) => {
          const userIds = (Array.isArray(usernames) ? usernames : [])
            .map((username: string) => findId(username))
            .filter(Boolean)

          acc[role as SymboleRasci] =
            role === SymboleRasci.APPROVER
              ? userIds[0] || null
              : userIds
          return acc
        },
        { ...EMPTY_SELECTION } as {
          [key in SymboleRasci]: string[] | string | null
        }
      )
      setSelectedUsers(initialSelectedUsers)
    }

    setReady(true)
  }, [isOpen, isUpdate, existingRasci, users, usersLoading])

  const isFormLoading = !ready

  const getAvailableUsers = (role: SymboleRasci) => {
    const selectedUserIds = Object.entries(selectedUsers)
      .filter(([key]) => key !== role)
      .flatMap(([_, value]) => (Array.isArray(value) ? value : [value]))
      .filter((id): id is string => id !== null)

    return users.filter(
      (user) =>
        user.enabled &&
        !selectedUserIds.includes(user.id) &&
        !hasRole(user.clientRoles, UserRole.OTHERS)
    )
  }

  const handleChange = (role: SymboleRasci, value: string[] | string) => {
    setSelectedUsers((prev) => ({
      ...prev,
      [role]:
        role === SymboleRasci.APPROVER
          ? (value as string)
          : (value as string[]),
    }))
  }

  const validateRasciSelection = (rasciData: any) => {
    const responsibles = rasciData?.RASCI?.[SymboleRasci.RESPONSIBLE] || []
    const approvers = rasciData?.RASCI?.[SymboleRasci.APPROVER] || []
    return responsibles.length > 0 && approvers.length > 0
  }

  const handleSave = async () => {
    const transformedRASCI: Record<string, string[]> = Object.entries(
      selectedUsers
    ).reduce(
      (acc, [key, value]) => {
        if (value === null) {
          acc[key] = []
        } else if (typeof value === 'string') {
          const user = users.find((u) => u.id === value)
          acc[key] = user ? [user.username] : []
        } else {
          acc[key] = value.map((userId) => {
            const user = users.find((u) => u.id === userId)
            return user ? user.username : ''
          })
        }
        return acc
      },
      {} as Record<string, string[]>
    )

    if (!validateRasciSelection({ RASCI: transformedRASCI })) {
      setAlert({
        message: t('popups.matrixRasci.validation.atLeastOneEach'),
        severity: 'warning',
        open: true,
      })
      return
    }

    try {
      if (isUpdate) {
        await dispatch(
          updateActeurActionAsync({
            actorId: (existingRasci?._id as string) || '',
            actionId,
            RASCI: transformedRASCI,
          })
        ).unwrap()
      } else {
        await dispatch(
          createActeurActionAsync({
            ActionId: actionId,
            RASCI: transformedRASCI,
          })
        ).unwrap()
      }
      setAlert({
        message: `Action ${isUpdate ? 'updated' : 'assigned'} to Rasci with success`,
        severity: 'success',
        open: true,
      })
      setTimeout(() => {
        navigateTo && navigate(returnTo || '/actions')
      }, 2000)
      onClose()
    } catch {
      setAlert({
        message: `Failed to ${isUpdate ? 'update' : 'assign'} action to Rasci. Please try again.`,
        severity: 'error',
        open: true,
      })
    }
  }

  const dismiss = () => {
    onClose()
    if (returnTo) navigate(returnTo)
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />
      <Dialog
        open={isOpen}
        onClose={dismiss}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: 'incyia-modal-paper' }}
      >
        <div className="incyia-modal-head">
          <div>
            <h3>
              {isUpdate
                ? t('popups.matrixRasci.dialog.titleUpdate')
                : t('popups.matrixRasci.dialog.titleAssign')}
            </h3>
            <p>
              {t('popups.matrixRasci.dialog.subtitle', {
                defaultValue:
                  'Assign Responsible and Approver at minimum. Other roles are optional.',
              })}
            </p>
          </div>
          <button
            type="button"
            className="incyia-modal-x"
            onClick={dismiss}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="incyia-modal-body">
          {warningMessage ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {warningMessage}
            </Alert>
          ) : null}

          {isFormLoading ? (
            <div className="rasci-edit-loading" role="status" aria-live="polite">
              <span className="rasci-edit-spinner" aria-hidden />
              <p>
                {t('popups.matrixRasci.dialog.loading', {
                  defaultValue: 'Loading RASCI assignments…',
                })}
              </p>
            </div>
          ) : (
            <div className="rasci-edit-list">
              {Object.entries(SymboleRasci).map(([key, role]) => {
                const meta = ROLE_META[role]
                const Icon = meta.Icon
                const isApprover = role === SymboleRasci.APPROVER

                return (
                  <div key={key} className="rasci-edit-row">
                    <div
                      className="rasci-edit-role"
                      style={{ background: meta.bg, color: meta.color }}
                    >
                      <Icon sx={{ fontSize: 16 }} />
                      <span>{t(`popups.matrixRasci.roles.${role}`)}</span>
                    </div>
                    <div className="rasci-edit-field">
                      <Select
                        className="rasci-edit-select"
                        multiple={!isApprover}
                        value={
                          selectedUsers[role] || (isApprover ? '' : [])
                        }
                        onChange={(e: SelectChangeEvent<string | string[]>) =>
                          handleChange(role, e.target.value)
                        }
                        displayEmpty
                        fullWidth
                        size="small"
                        MenuProps={{
                          autoFocus: false,
                          disableAutoFocusItem: true,
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                          marginThreshold: 12,
                          PaperProps: {
                            className: 'rasci-select-menu',
                            style: {
                              // Keep menu width locked to the select (MUI sets minWidth)
                              width: 0,
                              maxHeight: 280,
                            },
                          },
                          MenuListProps: {
                            className: 'rasci-select-menu-list',
                            dense: true,
                          },
                        }}
                        renderValue={(selected) => {
                          if (
                            (Array.isArray(selected) &&
                              selected.length === 0) ||
                            selected === ''
                          ) {
                            return (
                              <span className="rasci-select-placeholder">
                                {t('popups.matrixRasci.dialog.selectUsers')}
                              </span>
                            )
                          }
                          if (isApprover && typeof selected === 'string') {
                            const user = users.find((u) => u.id === selected)
                            return (
                              <Chip
                                label={
                                  user?.username ||
                                  t('popups.matrixRasci.dialog.selectUsers')
                                }
                                size="small"
                                className="rasci-edit-chip"
                              />
                            )
                          }
                          if (Array.isArray(selected)) {
                            return (
                              <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {selected.map((userId) => {
                                  const user = users.find(
                                    (u) => u.id === userId
                                  )
                                  return (
                                    <Chip
                                      key={userId}
                                      label={user?.username}
                                      size="small"
                                      className="rasci-edit-chip"
                                    />
                                  )
                                })}
                              </Box>
                            )
                          }
                          return (
                            <span className="rasci-select-placeholder">
                              {t('popups.matrixRasci.dialog.selectUsers')}
                            </span>
                          )
                        }}
                      >
                        {getAvailableUsers(role).map((user) => (
                          <MenuItem
                            key={user.id}
                            value={user.id}
                            className="rasci-select-option"
                          >
                            <span className="rasci-select-option-name">
                              {user.username}
                            </span>
                            <span className="rasci-edit-menu-role">
                              {user?.clientRoles?.[0] || '—'}
                            </span>
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="incyia-modal-foot">
          <DetailButton variant="ghost" onClick={dismiss}>
            {t('popups.matrixRasci.dialog.cancel')}
          </DetailButton>
          <DetailButton onClick={handleSave} disabled={isFormLoading}>
            {isUpdate
              ? t('popups.matrixRasci.dialog.update')
              : t('popups.matrixRasci.dialog.save')}
          </DetailButton>
        </div>
      </Dialog>
    </>
  )
}

export default ActorAction
